import { memo, useCallback, useEffect, useRef, useState } from "react";
import { Pressable, ActivityIndicator, Keyboard, Platform } from "react-native";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { useRouter } from "expo-router";
import { useCameraSearch } from "@visioni/a3s-dir/app";
import { useEnvironment } from "@src/config/environment-store";
import { directory } from "@src/config/runtime";
import { authenticatedFetch } from "@src/features/auth";
import { useAuthStore } from "@src/features/auth/auth-store";
import { View, Text, FormInput } from "@src/components";
import { Icon } from "@src/components/Icon";
import { useTenant } from "@src/features/tenants";
import { setLastUsedTenantIdSync } from "@src/features/tenants/last-used-tenant-store";
import { FEATURE_FLAGS, useBooleanFlag } from "@src/services/feature-flags/feature-flags";

interface CameraSearchWithCrossTenantProps {
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
  size?: "small" | "medium";
  onResultSelected?: () => void;
}

export const CameraSearchWithCrossTenant = memo(
  ({ searchQuery, onSearchQueryChange, size = "medium", onResultSelected }: CameraSearchWithCrossTenantProps) => {
    const { theme } = useUnistyles();
    const router = useRouter();
    const env = useEnvironment();
    const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
    const isEnabled = useBooleanFlag(FEATURE_FLAGS.CAMERA_SEARCH);
    const { selectedTenant, selectTenant } = useTenant();

    const [debouncedValue, setDebouncedValue] = useState("");
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (!searchQuery.trim()) {
        setDebouncedValue("");
        return;
      }
      timerRef.current = setTimeout(() => {
        setDebouncedValue(searchQuery.trim());
      }, 400);
      return () => {
        if (timerRef.current) clearTimeout(timerRef.current);
      };
    }, [searchQuery]);

    const searchAssetId =
      debouncedValue ?
        debouncedValue.toUpperCase().startsWith("CAM_") ?
          debouncedValue
        : `CAM_${debouncedValue}`
      : undefined;

    const { data, isFetching } = useCameraSearch({
      assetId: searchAssetId,
      authenticatedFetch,
      directoryBaseUrl: directory(env),
      enabled: isAuthenticated && isEnabled && !!debouncedValue,
    });

    const handleCameraPress = useCallback(
      (tenant: string, site: string, assetCameraKey: string) => {
        onSearchQueryChange("");
        Keyboard.dismiss();
        onResultSelected?.();
        const cameraPath = `/tenants/${tenant}/sites/${site}/cameras/${assetCameraKey}`;
        if (selectedTenant?.id !== tenant) {
          if (Platform.OS === "web" && typeof window !== "undefined") {
            setLastUsedTenantIdSync(tenant);
            const basePathPrefix =
              window.location.pathname === "/app" || window.location.pathname.startsWith("/app/") ? "/app" : "";
            window.location.pathname = `${basePathPrefix}${cameraPath}`;
            return;
          }
          selectTenant(tenant);
        }
        router.push(cameraPath as never);
      },
      [router, onSearchQueryChange, onResultSelected, selectedTenant, selectTenant],
    );

    const handleClear = useCallback(() => {
      onSearchQueryChange("");
      setDebouncedValue("");
    }, [onSearchQueryChange]);

    const hasResults = !!data?.result?.cameras.length;
    const searchComplete = isEnabled && !!debouncedValue && !isFetching && !!data;
    const showResults = isEnabled && !!debouncedValue && (isFetching || hasResults || searchComplete);

    return (
      <View style={styles.container}>
        <FormInput
          placeholder="Search cameras"
          value={searchQuery}
          onChangeText={onSearchQueryChange}
          size={size}
          returnKeyType="done"
          onSubmitEditing={() => Keyboard.dismiss()}
          iconLeft={<Icon name="search" size={20} color={theme.colors.textMuted} />}
          iconRight={
            searchQuery ?
              <Pressable onPress={handleClear} accessibilityRole="button" accessibilityLabel="Clear search">
                <Icon name="closeCircle" size={20} color={theme.colors.textMuted} />
              </Pressable>
            : undefined
          }
        />

        {showResults && (
          <View style={styles.results}>
            {isFetching ?
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="small" color={theme.colors.textMuted} />
              </View>
            : data?.result?.cameras.length ?
              <>
                <Text textStyle="xs/medium" color="textMuted" style={styles.resultLabel}>
                  {data.result.cameras.length} {data.result.cameras.length === 1 ? "camera" : "cameras"} found
                </Text>
                {data.result.cameras.map((cam) => (
                  <Pressable
                    key={cam.assetCameraKey}
                    onPress={() => handleCameraPress(data.result!.tenant, data.result!.site, cam.assetCameraKey)}
                    style={[styles.resultItem, { borderColor: theme.colors.borderCard }]}
                  >
                    <Icon name="video" size={16} color={theme.colors.icon} />
                    <View style={styles.resultText}>
                      <Text textStyle="sm/medium" color="text" numberOfLines={1}>
                        {cam.friendlyName ?? cam.camera}
                      </Text>
                      <Text textStyle="xxs/regular" color="textMuted" numberOfLines={1}>
                        {cam.assetCameraKey}
                      </Text>
                    </View>
                    <Icon name="chevronRight" size={16} color={theme.colors.textMuted} />
                  </Pressable>
                ))}
              </>
            : <View style={styles.emptyContainer}>
                <Icon name="search" size={40} color={theme.colors.textMuted} style={{ opacity: 0.5 }} />
                <Text textStyle="sm/medium" color="textMuted" textAlign="center">
                  No cameras found
                </Text>
                <Text textStyle="xs/regular" color="textMuted" textAlign="center">
                  The search requires an exact match on the camera digits. Check the number and try again.
                </Text>
              </View>
            }
          </View>
        )}
      </View>
    );
  },
);

CameraSearchWithCrossTenant.displayName = "CameraSearchWithCrossTenant";

const styles = StyleSheet.create((theme) => ({
  container: {
    gap: theme.spacing.lg,
  },
  results: {
    gap: theme.spacing.sm,
  },
  loadingContainer: {
    paddingVertical: theme.spacing["5xl"],
    alignItems: "center",
    justifyContent: "center",
  },
  emptyContainer: {
    paddingVertical: theme.spacing["5xl"],
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing.md,
  },
  resultLabel: {
    paddingVertical: theme.spacing.xs,
  },
  resultItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing.md,
    paddingVertical: theme.spacing.lg,
    paddingHorizontal: theme.spacing.lg,
    borderRadius: theme.spacing.sm,
    borderWidth: 1,
  },
  resultText: {
    flex: 1,
    gap: 2,
  },
}));
